/**
 * 67.25's footsteps, and 63.5's footsteps by the ground (ARCHITECTURE §5.13 "63.5, the design", reader 4): how loud a
 * person's step is, and so how far it carries — one rule for the sound (audio/gunfireMix.ts hears it) and, with
 * GameConfig.footstepsHeard, for the sim: a walker is heard as a shot is (`state.heard`, X1's signature at its own
 * range), at STEP_RANGE × its signature — so a bot hears a step exactly as far as a person would. A run is loudest, a walk
 * about half, crouched a third of that, prone a rustle; hard and crunching ground (rock, ice, snow, salt) carries a little
 * further, sand, mud and ash less. Pure.
 */
import type { GameState, PlayerState } from '../types';
import type { GroundMaterial } from '../types/ground';
import { groundMaterial, groundMaterialsOn } from './groundMaterial';
import { PERSON_BODY_HEIGHT, personSetOf } from './scaleProfile';

/** How a step is taken: the stance, how fast (the gait's run share 0 walk … 1 run), the ground's material, and wet (rain). */
export interface StepKind { stance: 'stand' | 'crouch' | 'prone'; run: number; material: GroundMaterial; wet: boolean }

/** A step is heard to this many people (a run's; a walk's, crouched or prone's are nearer — `footstepSignature`). */
export const STEP_RANGE = 22;

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/** How loud a step is, 0–1 (× STEP_RANGE people: how far it carries). */
export function footstepSignature(k: Pick<StepKind, 'stance' | 'run' | 'material'>): number {
  const pace = 0.45 + 0.55 * clamp(k.run, 0, 1);
  const stance = k.stance === 'prone' ? 0.2 : k.stance === 'crouch' ? 0.4 : 1;
  const ground: Partial<Record<GroundMaterial, number>> = { rock: 1.15, ice: 1.15, snow: 1.1, salt: 1.1, sand: 0.8, mud: 0.85, ash: 0.75 };
  return clamp(pace * stance * (ground[k.material] ?? 1), 0, 1);
}

/**
 * The sim's footstep (with GameConfig.footstepsHeard): how loud a step of `p` at `run` (its pace over its walking speed) is
 * as a share of the hearing range (`state.heard`'s `loud`) — STEP_RANGE × its signature people, over the person's range.
 * The ground is the clock's (63.5 step 6: the turn game's plain ground reads as soil).
 */
export function stepLoudness(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, p: Pick<PlayerState, 'planetIndex' | 'surfaceDir' | 'stance'>, run: number, actionSeconds: number): number {
  const ps = personSetOf(state);
  const material: GroundMaterial = groundMaterialsOn(state.config) ? groundMaterial(state, p.planetIndex, p.surfaceDir, actionSeconds) : 'soil';
  const stance = p.stance === 'crouch' ? 'crouch' : p.stance === 'prone' ? 'prone' : 'stand';
  return (STEP_RANGE * footstepSignature({ stance, run, material }) * PERSON_BODY_HEIGHT * ps.k) / ps.hearRange;
}
